import { AgFilterComponent } from '@ag-grid-community/angular';
import { IDoesFilterPassParams, IFilterParams, IRowNode } from '@ag-grid-community/core';
import {
	ChangeDetectionStrategy, Component, HostBinding, OnDestroy, ViewEncapsulation
} from '@angular/core';
import { FormControl, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { debounceTime, distinctUntilChanged, Subscription } from 'rxjs';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { NgIf } from '@angular/common';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { TranslateModule } from '@ngx-translate/core';
import { ActivatedRoute } from '@angular/router';

import { NumberFilter, NumberRange, FilterType } from '../../models/ag-grid';
import { DatacenterType } from '../../models/datacenter-aggrid-view';
import { ColDefWithFields } from '../column-type';
import { FilterInstance } from '../../services/datacenter-filter';
import { isNumber, NUMBER_REGEX } from '../../utils/number.format';

const SHIPMENT_MULTI_VALUE_NUMBER_FILTER_NAME =
	<NumberFilter['customFilterName']> 'shipment-multi-value-number-filter';

@Component({
	selector: 'copo-datacenter-number-filter',
	templateUrl: './datacenter-number-filter.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	encapsulation: ViewEncapsulation.Emulated,
	imports: [
		TranslateModule,

		MatFormFieldModule,

		MatInputModule,
		FormsModule,
		ReactiveFormsModule,
		NgIf,

		MatButtonModule,
		MatIconModule
	]
})
export class DatacenterNumberFilterComponent implements AgFilterComponent, FilterInstance, OnDestroy {
	@HostBinding('class.passthrough') readonly passthrough = true;

	lessThanTextControl = new FormControl('', {
		nonNullable: true,
		validators: Validators.pattern(NUMBER_REGEX)
	});

	moreThanTextControl = new FormControl('', {
		nonNullable: true,
		validators: Validators.pattern(NUMBER_REGEX)
	});

	private isClientSide = false;
	private model: NumberFilter = { customFilterName: FilterType.NUMBER_FILTER };
	private params?: IFilterParams<Record<string, unknown>, unknown>;
	private readonly lessThanSubscription: Subscription;
	private readonly moreThanSubscription: Subscription;

	constructor(private readonly route: ActivatedRoute) {
		this.lessThanSubscription = this.lessThanTextControl.valueChanges.pipe(
			debounceTime(500),
			distinctUntilChanged()
		).subscribe(value => {
			this.model.lessThan = this.parseValue(value);
			if (this.params) {
				this.params.filterChangedCallback();
			}
		});
		this.moreThanSubscription = this.moreThanTextControl.valueChanges.pipe(
			debounceTime(500),
			distinctUntilChanged()
		).subscribe(value => {
			this.model.moreThan = this.parseValue(value);
			if (this.params) {
				this.params.filterChangedCallback();
			}
		});
	}

	ngOnDestroy() {
		this.lessThanSubscription.unsubscribe();
		this.moreThanSubscription.unsubscribe();
	}

	agInit(params: IFilterParams<Record<string, unknown>>) {
		this.params = params;
		this.model.customFilterName = this.getCustomFilterName();
		const rowModelType = params.rowModel.getType();
		this.isClientSide = rowModelType === 'clientSide';
	}

	isFilterActive() {
		return this.model.lessThan !== undefined
			|| this.model.moreThan !== undefined
			|| (this.model.ranges !== undefined && this.model.ranges.length > 0);
	}

	doesFilterPass(filterParams: IDoesFilterPassParams<Record<string, unknown>>) {
		if (!this.isClientSide) {
			return this.isFilterActive();
		}
		const value = this.getColumnValue(filterParams.node);
		if (value === undefined) {
			return false;
		}
		if (this.model.ranges?.length && !this.model.ranges.some(range => this.isInRange(value, range))) {
			return false;
		}
		return this.isInRange(value, this.model);
	}

	getModel(): NumberFilter | null {
		if (!this.isFilterActive()) {
			return null;
		}
		return { ...this.model, customFilterName: this.getCustomFilterName() };
	}

	setModel(model: NumberFilter | null) {
		this.model = { ...model, customFilterName: this.getCustomFilterName() };
		this.lessThanTextControl.setValue(model?.lessThan !== undefined ? String(model.lessThan) : '');
		this.moreThanTextControl.setValue(model?.moreThan !== undefined ? String(model.moreThan) : '');
	}

	getCustomFilterName(): NumberFilter['customFilterName'] {
		const filterParams = <{ filterType?: string } | undefined> this.params?.colDef.filterParams;
		if (filterParams?.filterType === 'SHIPMENT_MULTI_VALUE_NUMBER_FILTER') {
			return SHIPMENT_MULTI_VALUE_NUMBER_FILTER_NAME;
		}
		return this.isShipmentMultiLineColumn()
			? SHIPMENT_MULTI_VALUE_NUMBER_FILTER_NAME
			: FilterType.NUMBER_FILTER;
	}

	resetLessThen() {
		this.model.lessThan = undefined;
		this.lessThanTextControl.reset();
	}

	resetMoreThen() {
		this.model.moreThan = undefined;
		this.moreThanTextControl.reset();
	}

	private parseValue(value: string | null): number | undefined {
		if (!value) {
			return undefined;
		}
		if (!isNumber(value)) {
			return undefined;
		}
		const num = Number(value);
		if (Number.isNaN(num)) {
			return undefined;
		}

		const { params } = this;
		if (!params) {
			return num;
		}

		if (params.colDef.colId === 'producedLengthDimensionConcat') {
			return num * 1000;
		}
		return num;
	}

	private getColumnValue(node: IRowNode<Record<string, unknown>>): number | undefined {
		if (!this.params || typeof node.data !== 'object') {
			return undefined;
		}
		const { data } = node;
		if (typeof this.params.colDef.valueGetter === 'function') {
			const value = <unknown> this.params.colDef.valueGetter({
				api: this.params.api,
				colDef: this.params.colDef,
				column: this.params.column,
				context: this.params.context,
				data,
				node,
				getValue(field: string) {
					return data[field];
				}
			});
			return typeof value === 'number' ? value : undefined;
		} else if (this.params.colDef.field) {
			const value = data[this.params.colDef.field];
			return typeof value === 'number' ? value : undefined;
		}
		return undefined;
	}

	private isInRange(value: number, range: NumberRange) {
		/* more readable like that */
		/* eslint-disable sonarjs/prefer-single-boolean-return */
		if (range.lessThan !== undefined && value > range.lessThan) {
			return false;
		}
		if (range.moreThan !== undefined && value < range.moreThan) {
			return false;
		}
		return true;
		/* eslint-enable sonarjs/prefer-single-boolean-return */
	}

	private isShipmentMultiLineColumn() {
		const datacenterType = <DatacenterType | undefined> this.route.snapshot.data['type'];
		const colDef = <ColDefWithFields<Record<string, unknown>> | undefined> this.params?.colDef;
		return datacenterType === 'shipment' && colDef?.valueType !== undefined;
	}
}
